import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const initialFormData = {
    assetName: '',
    brand: '',
    description: '',
    purchaseDate: '',
    warrantyExpiry: '',
    status: 'Active'
};

const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';

    if (
        dateString.includes('-') &&
        dateString.split('-')[0].length === 2
    ) {
        return dateString;
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    if (
        dateString.includes('-') &&
        dateString.split('-')[0].length === 2
    ) {
        const [day, month, year] = dateString.split('-');

        return `${year}-${month}-${day}`;
    }

    return dateString.split('T')[0];
};

const StoreAssetsEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    useEffect(() => {
        const assetData = location.state?.asset;

        if (assetData) {
            setFormData({
                assetName: assetData.assetName || '',
                brand: assetData.brand || '',
                description: assetData.description || '',
                purchaseDate: formatDateForInput(
                    assetData.purchaseDate
                ),
                warrantyExpiry: formatDateForInput(
                    assetData.warrantyExpiry
                ),
                status: assetData.status || 'Active'
            });
        }
        if (id) {
            getAssetDetails();
        }
    }, [id]);

    const getAssetDetails = async () => {
        console.log('Calling get asset by id API:', id);

        try {
            const response = await axios.post(
                API_URL,
                {
                    action: 'get-by-id-asset',
                    id
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Get asset response:', response.data);

            const result = response.data;

            if (result?.success && result?.data) {
                const asset = result.data;

                setFormData({
                    assetName: asset.assetName || '',
                    brand: asset.brand || '',
                    description: asset.description || '',
                    purchaseDate: formatDateForInput(
                        asset.purchaseDate
                    ),
                    warrantyExpiry: formatDateForInput(
                        asset.warrantyExpiry
                    ),
                    status: asset.status || 'Active'
                });
            } else {
                toast.error(
                    result?.message ||
                        'Failed to fetch asset details'
                );
            }
        } catch (error) {
            console.error('Error fetching asset:', error);

            toast.error(
                error.response?.data?.message ||
                    'Failed to fetch asset details'
            );
        } finally {
            setPageLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.assetName.trim()) {
            newErrors.assetName = 'Asset name is required';
        }

        if (!formData.brand.trim()) {
            newErrors.brand = 'Brand is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.purchaseDate) {
            newErrors.purchaseDate = 'Purchase date is required';
        }

        if (!formData.warrantyExpiry) {
            newErrors.warrantyExpiry =
                'Warranty expiry is required';
        }

        if (
            formData.purchaseDate &&
            formData.warrantyExpiry &&
            new Date(formData.warrantyExpiry) <
                new Date(formData.purchaseDate)
        ) {
            newErrors.warrantyExpiry =
                'Warranty expiry cannot be before purchase date';
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fill all required fields');
            return;
        }

        setLoading(true);

        try {
            const payload = {
                action: 'update-asset',
                id,
                assetName: formData.assetName.trim(),
                brand: formData.brand.trim(),
                description: formData.description.trim(),
                purchaseDate: formatDateToDDMMYYYY(
                    formData.purchaseDate
                ),
                warrantyExpiry: formatDateToDDMMYYYY(
                    formData.warrantyExpiry
                ),
                status: formData.status
            };


            const response = await axios.post(
                API_URL,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            const result = response.data;

            if (result?.success) {
                toast.success(
                    result.message ||
                        'Asset updated successfully'
                );

                navigate('/store-assets');
            } else {
                toast.error(
                    result?.message ||
                        'Failed to update asset'
                );
            }
        } catch (error) {
            console.error('Error updating asset:', error);

            toast.error(
                error.response?.data?.message ||
                    error.message ||
                    'Something went wrong'
            );
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                Edit Store Asset
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>

                            <p className="employee-description">
                                Update asset details and inventory
                                information.
                            </p>
                        </div>

                        <div className="col-md-4 employee-view-header-actions">
                            <button
                                type="button"
                                className="employee-back-btn"
                                onClick={() =>
                                    navigate('/store-assets')
                                }
                                disabled={loading}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                    

                    <div className="card employee-content-card">
                        <div className="card-body">
                              {loading || pageLoading ? (
                               <Loading size={120} />
                                ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <h4 className="section-title">
                                        Asset Information
                                    </h4>

                                    <p className="section-subtitle">
                                        Update the store asset details
                                        below.
                                    </p>

                                    <div
                                        className="form-row-3"
                                        style={{
                                            gridTemplateColumns:
                                                'repeat(4, 1fr)'
                                        }}
                                    >
                                        <div className="form-group">
                                            <label className="form-label">
                                                Asset Name
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="text"
                                                name="assetName"
                                                className={`form-control ${
                                                    errors.assetName
                                                        ? 'is-invalid'
                                                        : ''
                                                }`}
                                                value={
                                                    formData.assetName
                                                }
                                                onChange={handleChange}
                                                placeholder="Enter asset name"
                                            />

                                            {errors.assetName && (
                                                <div className="invalid-feedback">
                                                    {
                                                        errors.assetName
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">
                                                Brand
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="text"
                                                name="brand"
                                                className={`form-control ${
                                                    errors.brand
                                                        ? 'is-invalid'
                                                        : ''
                                                }`}
                                                value={formData.brand}
                                                onChange={handleChange}
                                                placeholder="Enter brand"
                                            />

                                            {errors.brand && (
                                                <div className="invalid-feedback">
                                                    {errors.brand}
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">
                                                Purchase Date
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="date"
                                                name="purchaseDate"
                                                className={`form-control ${
                                                    errors.purchaseDate
                                                        ? 'is-invalid'
                                                        : ''
                                                }`}
                                                value={
                                                    formData.purchaseDate
                                                }
                                                onChange={handleChange}
                                            />

                                            {errors.purchaseDate && (
                                                <div className="invalid-feedback">
                                                    {
                                                        errors.purchaseDate
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">
                                                Warranty Expiry
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="date"
                                                name="warrantyExpiry"
                                                className={`form-control ${
                                                    errors.warrantyExpiry
                                                        ? 'is-invalid'
                                                        : ''
                                                }`}
                                                value={
                                                    formData.warrantyExpiry
                                                }
                                                onChange={handleChange}
                                                min={
                                                    formData.purchaseDate
                                                }
                                            />

                                            {errors.warrantyExpiry && (
                                                <div className="invalid-feedback">
                                                    {
                                                        errors.warrantyExpiry
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <div className="form-group">
                                        <label className="form-label">
                                            Description
                                            <span className="required-star">
                                                *
                                            </span>
                                        </label>

                                        <textarea
                                            name="description"
                                            className={`form-control ${
                                                errors.description
                                                    ? 'is-invalid'
                                                    : ''
                                            }`}
                                            value={
                                                formData.description
                                            }
                                            onChange={handleChange}
                                            placeholder="Add model, serial number, or any identifying details."
                                            rows="4"
                                        />

                                        {errors.description && (
                                            <div className="invalid-feedback">
                                                {
                                                    errors.description
                                                }
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={() =>
                                            navigate('/store-assets')
                                        }
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? 'Updating...'
                                            : 'Update Asset'}
                                    </button>
                                </div>
                            </form>
                          )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default StoreAssetsEdit;

